import type { SiteSettings } from './types';

export interface AboutPageValue {
  title: string;
  description: string;
}

export interface AboutPageMilestone {
  year: string;
  title: string;
  description: string;
}

export interface AboutPageContent {
  heroTitle: string;
  heroSubtitle: string;
  heroImageUrl: string;
  missionTitle: string;
  missionText: string;
  visionTitle: string;
  visionText: string;
  valuesHeading: string;
  valuesIntro: string;
  values: AboutPageValue[];
  milestonesHeading: string;
  milestonesIntro: string;
  milestones: AboutPageMilestone[];
  ctaHeading: string;
  ctaButtonText: string;
  ctaLink: string;
}

const DEFAULT_VALUES: AboutPageValue[] = [
  {
    title: 'Compassion',
    description: 'We approach every community with empathy, respect, and cultural sensitivity.',
  },
  {
    title: 'Accountability',
    description: 'We are transparent in our operations and accountable to the communities we serve.',
  },
  {
    title: 'Inclusivity',
    description: 'We ensure all programs are accessible to everyone, regardless of gender, age, or background.',
  },
  {
    title: 'Excellence',
    description: 'We strive for the highest standards in everything we do, from planning to execution.',
  },
];

const DEFAULT_MILESTONES: AboutPageMilestone[] = [
  {
    year: '2015',
    title: 'Founded',
    description: 'Kisapuk Community Initiative was established by a group of passionate local leaders.',
  },
  {
    year: '2017',
    title: 'First Water Project',
    description: 'Completed our first borehole, serving 500 households with clean water.',
  },
  {
    year: '2019',
    title: 'Education Program',
    description: 'Launched our scholarship fund, awarding 50 scholarships in the first year.',
  },
  {
    year: '2021',
    title: 'Environmental Drive',
    description: 'Began our reforestation initiative, planting 5,000 trees in the first year.',
  },
  {
    year: '2024',
    title: '10,000 Trees',
    description: 'Surpassed our tree-planting goal and expanded to 12 sites across the region.',
  },
];

export function getDefaultAboutPageContent(): AboutPageContent {
  return {
    heroTitle: 'Our Story',
    heroSubtitle:
      "For nearly a decade, we've been working hand-in-hand with communities to create sustainable, lasting change.",
    heroImageUrl: 'https://images.pexels.com/photos/3184338/pexels-photo-3184338.jpeg',
    missionTitle: 'Our Mission',
    missionText:
      'To create lasting, positive change in underserved communities by empowering individuals with the tools, knowledge, and resources they need to build a better future for themselves and generations to come.',
    visionTitle: 'Our Vision',
    visionText:
      'A future where every community in the Rift Valley has access to clean water, quality education, sustainable livelihoods, and a healthy environment — thriving in dignity and self-reliance.',
    valuesHeading: 'What Drives Us Forward',
    valuesIntro: '',
    values: DEFAULT_VALUES.map((value) => ({ ...value })),
    milestonesHeading: 'Milestones Over the Years',
    milestonesIntro: '',
    milestones: DEFAULT_MILESTONES.map((milestone) => ({ ...milestone })),
    ctaHeading: 'Want to be part of our story?',
    ctaButtonText: 'Get in Touch',
    ctaLink: '/contact',
  };
}

export function getAboutPageContent(settings?: SiteSettings): AboutPageContent {
  const raw = settings?.about_page_content;
  if (!raw) {
    return getDefaultAboutPageContent();
  }

  try {
    const parsed = JSON.parse(raw) as Partial<AboutPageContent>;
    const defaults = getDefaultAboutPageContent();

    return {
      ...defaults,
      ...parsed,
      values: Array.isArray(parsed.values) && parsed.values.length > 0
        ? parsed.values.map((value) => ({
            title: value?.title || '',
            description: value?.description || '',
          }))
        : defaults.values,
      milestones: Array.isArray(parsed.milestones) && parsed.milestones.length > 0
        ? parsed.milestones.map((milestone) => ({
            year: milestone?.year || '',
            title: milestone?.title || '',
            description: milestone?.description || '',
          }))
        : defaults.milestones,
    };
  } catch {
    return getDefaultAboutPageContent();
  }
}

export function stringifyAboutPageContent(content: AboutPageContent): string {
  return JSON.stringify(content, null, 2);
}
